import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewChild,
} from "@angular/core";
import {
	AbstractControl,
	UntypedFormArray,
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	ValidatorFn,
	Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatExpansionPanelHeader } from "@angular/material/expansion";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { isEmpty, String } from "lodash";
import moment from "moment";
import { Subscription } from "rxjs";

import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { FormDataOptions, TS1Form, TS1FormRequestJSON, TS1FormResponseJSON } from "src/app/models/models";

import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { patchReminderNotesFormArray } from "src/app/cbta-web/ets1/reminder-notes/ets1-reminder-notes-form.utils";
import {
	isEts1SessionV2,
	mapApiRowsToEts1SessionConfig,
	patchEts1SessionSelectionFromTemplate,
	resolveEts1SessionAocCode,
	syncEts1SessionFormControls,
} from "src/app/cbta-web/ets1/session/ets1-session-form.utils";
import {
	buildEts1SessionColumnTree,
	Ets1SessionColumnVM,
} from "src/app/cbta-web/ets1/session/ets1-session-layout.utils";
import {
	hasEts1RadioRequiredErrors,
	missingRequiredSessionText as hasMissingRequiredSessionText,
	hasSessionRemarkRequiredErrors,
	markMissingRequiredEts1RadioSelection,
} from "src/app/cbta-web/ets1/session/ets1-session-validation.utils";
import {
	buildEts35SessionSections,
	enforceEts35CoursePurposeSingleSelection,
	Ets35SessionSectionVM,
	ets35TrainingColumns,
	syncEts35AdminSessionControls,
} from "src/app/cbta-web/ets1/session/ets35-session-sections.utils";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import {
	ErrorSnackbarComponent,
	ErrorSnackbarData,
} from "src/app/shared/components/cbta-snackbar/error-snackbar.component";
import { HasUnsavedFormChanges } from "src/app/shared/interfaces/has-unsaved-form-changes";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { resolveHttpErrorMessageText } from "src/app/shared/utils/http-error-message";
import { inferFormConfigVersion } from "../../ratings/container-ratings-form/shared-v2/utils/form-config-version.utils";
import {
	appendTs1CompetencyAssessments,
	buildAdminFormSessionPayload,
	buildManeuverV2FromObservations,
	buildSessionRemarksByCode,
	buildTs1SessionColumns,
	buildV2RequestPayload,
	filterTs1ManeuverOptions,
	isEts35FormType,
	isTs1FormType,
	parseFormConfigResponse,
	patchAttemptsIntoManeuverV2,
	patchTs1FormConfigFromResponse,
	patchTs1SessionRemarksFromResponse,
	sortSessionsAlphabetically,
	splitTs1AssessmentConfig,
	syncTs1SessionRemarkControls,
	Ts1AssessmentConfigEntry,
	unwrapFormApiResponse,
	wireTs1ReminderNotesClearing,
	wireTs1SessionCheckboxRemarkClearing,
} from "../ts1-v2/ts1-form.factories";
type Observation = { code: string; description?: string; fixed_maneuver?: string };

type SessionCfgRaw = {
	code: string;
	description: string;
	session_conf_type?: { code: string; description: string };
};

type SessionCfg = { code: string; description: string; requiresText?: boolean };
type SessionGroup = { code: string; key: string; items: SessionCfg[] };

@Component({
	selector: "app-edit-form",
	templateUrl: "./edit-form.component.html",
	styleUrls: ["./edit-form.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class EditFormComponent implements OnInit, OnDestroy, HasUnsavedFormChanges {
	createNewForm!: UntypedFormGroup;

	today = new Date();
	currentDate: Date = new Date();

	@ViewChild("dateStartInput") dateStartInput?: ElementRef;
	@ViewChild("dateEndInput") dateEndInput?: ElementRef;

	private routeParamSubscription!: Subscription;
	private notReqSub?: Subscription;
	private otherSessionSubs: Subscription[] = [];
	private sessionRemarkSubs: Subscription[] = [];
	private ets1SessionSubs: Subscription[] = [];
	private reminderNotesSubs: Subscription[] = [];
	formId = "";
	formSuccess = false;
	formErrorMessage = "";
	isSubmitting = false;

	// sessions UI state
	sessionGroups: SessionGroup[] = [];
	singleCols: SessionCfg[][] = [];
	ts1SessionCols: SessionCfg[][] = [];
	ets1SessionGroups: SessionGroup[] = [];
	ets1SessionColumns: Ets1SessionColumnVM[] = [];
	ets35SessionSections: Ets35SessionSectionVM[] = [];
	twoColsLeft?: SessionGroup;
	twoColsRight: SessionCfg[][] = [];
	secondGroupTitle = "";
	twoGroupsCompact = false;
	lastColTitle = "";
	lastCols?: SessionCfg[][] = [];
	ts1FormManeuversOptions: any[] = [];
	isTS1V2Form = false;
	formIsPublished = false;
	private preservedCompetencyAssessments: Ts1AssessmentConfigEntry[] = [];

	// maneuver display metadata (for non-TS1 headers)
	fixedManeuvers: Array<{ code: string; description: string; subDescription: string }> = [];

	// cache all observations from config (for Add New)
	private lastObservations: Observation[] = [];

	DATE_FORMAT = CONSTANT.DATE_FORMAT;
	private readonly DISPLAY_FMT = this.DATE_FORMAT.INPUT_DISPLAY_DATE_FORMAT; // "DD MMM YYYY"

	// Keys that are not OB categories on a maneuver FG
	private readonly BASE_OB_KEYS = new Set([
		"maneuver",
		"maneuverCode",
		"assessmentId",
		"traineeAssessmentId",
		"candidateId",
		"selectedOBs",
		"safetyOutcome",
		"repeat",
		"formType",
	]);

	constructor(
		private fb: UntypedFormBuilder,
		private httpService: HttpService,
		private cd: ChangeDetectorRef,
		private loadingService: LoaderService,
		public sharedService: SharedService,
		private route: ActivatedRoute,
		private router: Router,
		private roleService: RoleService,
		private dialog: MatDialog,
		private snackBar: MatSnackBar,
	) {
		this.initForm();
	}

	scrollToTop(): void {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.FORMS);

		// Keep the per-maneuver validator in sync with current form type (TS-1 only)
		this.formType.valueChanges.subscribe(() => {
			this.refreshManeuverValidatorsForFormType();
			this.maneuvers.markAllAsTouched();
			this.maneuvers.updateValueAndValidity({ onlySelf: false });
			this.cd.markForCheck();
		});

		this.routeParamSubscription = this.route.params.subscribe((params) => {
			this.formId = params["id"];
			if (!isEmpty(this.formId)) this.fetchFormById();
		});
	}

	ngOnDestroy(): void {
		this.teardownSessionSubscriptions();
		if (this.routeParamSubscription) this.routeParamSubscription.unsubscribe();
	}

	// ---------- Validators ----------
	private requireAtLeastOneSession(): ValidatorFn {
		return (ctrl: AbstractControl) => {
			const g = ctrl as UntypedFormGroup;
			const hasOne = Object.values(g.controls || {}).some((c: any) => c?.value === true);
			return hasOne ? null : { noSessionSelected: true };
		};
	}

	/** Refresh per-maneuver validators when formType changes */
	private refreshManeuverValidatorsForFormType(): void {
		for (const ctrl of this.maneuvers.controls) {
			const mfg = ctrl as UntypedFormGroup;
			mfg.updateValueAndValidity({ emitEvent: false });
		}
		this.maneuvers.updateValueAndValidity({ emitEvent: false });
	}

	// ---------- Init ----------
	private initForm(): void {
		this.createNewForm = this.fb.group({
			formType: ["", [Validators.required]],
			formName: ["", [Validators.required]],
			syllabusStartDate: [null, [Validators.required]],
			syllabusEndDate: [null, [Validators.required]],
			sessions: this.fb.group({}),
			sessionRemarks: this.fb.group({}),
			ets1SessionVersion: [1],
			reminderNotesEnabled: [false],
			reminderNotesTemplate: [""],
			reminderNotes: this.fb.array([]),
			maneuvers: this.fb.array([]),
		});

		// Section validators
		this.sessions.addValidators(this.requireAtLeastOneSession());
		// NOTE: no array-level "any maneuver has OB" validator — we validate per panel instead.
	}

	// ---------- Getters ----------
	get formType() {
		return this.createNewForm.get("formType")!;
	}
	get formName() {
		return this.createNewForm.get("formName")!;
	}
	get syllabusStartDate() {
		return this.createNewForm.get("syllabusStartDate")! as UntypedFormControl;
	}
	get syllabusEndDate() {
		return this.createNewForm.get("syllabusEndDate")! as UntypedFormControl;
	}
	get sessions(): UntypedFormGroup {
		return this.createNewForm.get("sessions") as UntypedFormGroup;
	}
	get sessionRemarks(): UntypedFormGroup {
		return this.createNewForm.get("sessionRemarks") as UntypedFormGroup;
	}
	get ets1SessionVersion(): number {
		return Number(this.createNewForm?.get("ets1SessionVersion")?.value ?? 1);
	}

	get isEts1V2(): boolean {
		return isEts1SessionV2({ ets1SessionVersion: this.ets1SessionVersion });
	}
	get reminderNotes(): UntypedFormArray {
		return this.createNewForm.get("reminderNotes") as UntypedFormArray;
	}
	get reminderNotesEnabled(): UntypedFormControl {
		return this.createNewForm.get("reminderNotesEnabled") as UntypedFormControl;
	}
	get reminderNotesTemplate(): UntypedFormControl {
		return this.createNewForm.get("reminderNotesTemplate") as UntypedFormControl;
	}
	get maneuvers(): UntypedFormArray {
		return this.createNewForm.get("maneuvers") as UntypedFormArray;
	}

	get hasSessionRemarkRequiredErrors(): boolean {
		return (this.isTS1V2 || this.isEts35SessionCatalog) && hasSessionRemarkRequiredErrors(this.sessionRemarks);
	}

	get hasEts1RadioRequiredErrors(): boolean {
		return (this.isEts1V2 && this.isTS1) || this.isEts35SessionCatalog
			? hasEts1RadioRequiredErrors(this.sessions)
			: false;
	}

	get isEts35SessionCatalog(): boolean {
		return (
			isEts35FormType(this.formType.value as FormDataOptions | string | null) && this.ets35SessionSections.length > 0
		);
	}

	// Flags
	get isTS1(): boolean {
		return isTs1FormType(this.formType.value as FormDataOptions | string | null);
	}
	get isTS6(): boolean {
		const v = this.formType.value as FormDataOptions | null;
		return !!v && v.code === CONSTANT.FORM_TYPE.ts_6;
	}
	get isCabinCrew(): boolean {
		const v = this.formType.value as FormDataOptions | null;
		return !!v && v.code === CONSTANT.FORM_TYPE.cabin_crew;
	}
	get isCabinCrewInstructor(): boolean {
		const v = this.formType.value as FormDataOptions | null;
		return !!v && v.code === CONSTANT.FORM_TYPE.cabin_crew_instructor;
	}

	get isTS1V2(): boolean {
		return this.isTS1 && this.isTS1V2Form;
	}

	get isReadOnly(): boolean {
		return this.roleService.isGroupAdmin;
	}

	@HostBinding("class.edit-form--view-only")
	get editFormViewOnlyClass(): boolean {
		return this.isReadOnly;
	}

	// Datepicker bindings (Date objects)
	get trainingStartDate(): Date {
		const raw = this.syllabusStartDate.value;
		return raw ? moment(raw).toDate() : this.today;
	}
	get trainingEndDate(): Date {
		const raw = this.syllabusEndDate.value;
		return raw ? moment(raw).toDate() : this.today;
	}

	// ---------- Sessions helpers ----------
	private splitIntoColumns<T>(items: T[], columns: number): T[][] {
		if (columns <= 0) return [];
		if (columns === 1) return [items.slice()];

		if (columns === 2) {
			const n = items.length;
			// Even-ish split, but first column must have at least 5 (if available)
			const firstCount = Math.min(n, Math.max(5, Math.ceil(n / 2)));
			return [items.slice(0, firstCount), items.slice(firstCount)];
		}

		// Fallback for 3+ columns (simple round-robin or keep your previous logic)
		const cols: T[][] = Array.from({ length: columns }, () => []);
		items.forEach((item, idx) => cols[idx % columns].push(item));
		return cols;
	}

	private clearSessionControls() {
		Object.keys(this.sessions.controls).forEach((k) => this.sessions.removeControl(k));
	}

	private teardownSessionSubscriptions() {
		if (this.notReqSub) {
			this.notReqSub.unsubscribe();
			this.notReqSub = undefined;
		}
		this.otherSessionSubs.forEach((s) => s.unsubscribe());
		this.otherSessionSubs = [];
		this.sessionRemarkSubs.forEach((s) => s.unsubscribe());
		this.sessionRemarkSubs = [];
		this.reminderNotesSubs.forEach((s) => s.unsubscribe());
		this.reminderNotesSubs = [];
		this.ets1SessionSubs.forEach((s) => s.unsubscribe());
		this.ets1SessionSubs = [];
	}

	private wireUpNotRequiredMutualExclusion() {
		this.teardownSessionSubscriptions();

		const notReq = this.sessions.get("not_required") as UntypedFormControl | null;
		if (!notReq) return;

		this.notReqSub = notReq.valueChanges.subscribe((val: boolean) => {
			if (val !== true) return;
			Object.keys(this.sessions.controls).forEach((k) => {
				if (k === "not_required") return;
				const c = this.sessions.get(k) as UntypedFormControl;
				if (c?.value === true) c.setValue(false, { emitEvent: false });
			});
			this.sessions.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		});

		Object.keys(this.sessions.controls).forEach((k) => {
			if (k === "not_required") return;
			const c = this.sessions.get(k) as UntypedFormControl;
			if (!c) return;

			const sub = c.valueChanges.subscribe((val: boolean) => {
				if (val === true && notReq.value === true) {
					notReq.setValue(false, { emitEvent: false });
					this.sessions.updateValueAndValidity({ onlySelf: true, emitEvent: false });
				}
			});
			this.otherSessionSubs.push(sub);
		});
	}

	private rebuildEts1AocSessionControls(cfgs: SessionCfgRaw[]): void {
		this.teardownSessionSubscriptions();
		this.clearSessionControls();
		Object.keys(this.sessionRemarks.controls).forEach((k) => this.sessionRemarks.removeControl(k));
		this.ets1SessionGroups = [];

		const catalog = mapApiRowsToEts1SessionConfig(cfgs);
		this.ets1SessionColumns = buildEts1SessionColumnTree(catalog);
		syncEts1SessionFormControls(
			this.fb,
			this.sessions,
			this.sessionRemarks,
			this.ets1SessionColumns,
			this.ets1SessionSubs,
		);
		this.ts1SessionCols = [];
		this.sessionGroups = [];
		this.ets35SessionSections = [];
		this.sessions.updateValueAndValidity({ onlySelf: true });
	}

	private rebuildEts35SessionControls(cfgs: SessionCfgRaw[]): void {
		this.teardownSessionSubscriptions();
		this.clearSessionControls();
		Object.keys(this.sessionRemarks.controls).forEach((key) => this.sessionRemarks.removeControl(key));
		this.ets35SessionSections = buildEts35SessionSections(cfgs);
		syncEts35AdminSessionControls(
			this.fb,
			this.sessions,
			this.sessionRemarks,
			this.ets35SessionSections,
			this.ets1SessionSubs,
		);
		this.ets1SessionColumns = [];
		this.ts1SessionCols = [];
		this.sessionGroups = [];
		this.sessions.updateValueAndValidity({ onlySelf: true });
	}

	private rebuildSessionControls(cfgs: SessionCfgRaw[], ts1FlatLayout = false) {
		this.teardownSessionSubscriptions();
		this.clearSessionControls();
		Object.keys(this.sessionRemarks.controls).forEach((k) => this.sessionRemarks.removeControl(k));
		this.ets35SessionSections = [];
		this.ts1SessionCols = [];

		if (ts1FlatLayout) {
			this.ets1SessionGroups = [];
			const isOthers = (s: SessionCfgRaw) => s.session_conf_type?.code?.toLowerCase() === "other";
			const ts1Sessions = cfgs.filter((s) => !isOthers(s));
			const items = sortSessionsAlphabetically(ts1Sessions);
			items.forEach((item) => this.sessions.addControl(item.code, new UntypedFormControl(false)));
			syncTs1SessionRemarkControls(
				this.fb,
				this.sessionRemarks,
				items.map((i) => i.code),
			);
			wireTs1SessionCheckboxRemarkClearing(this.sessions, this.sessionRemarks, this.sessionRemarkSubs);

			this.ts1SessionCols = buildTs1SessionColumns(ts1Sessions);
			this.sessionGroups = [];
			this.singleCols = [];
			this.twoColsLeft = undefined;
			this.twoColsRight = [];
			this.twoGroupsCompact = false;
			this.secondGroupTitle = "";
			this.lastCols = [];
			this.lastColTitle = "";

			this.sessions.updateValueAndValidity({ onlySelf: true });
			return;
		}

		const grouped = new Map<string, SessionCfg[]>();
		const groupedCode = new Map<string, string>();

		cfgs.forEach((raw) => {
			const key = raw.session_conf_type?.description || "Others";
			const gCode = raw.session_conf_type?.code || "others";
			if (!grouped.has(key)) grouped.set(key, []);
			grouped.get(key)!.push({ code: raw.code, description: raw.description });
			if (!groupedCode.has(key)) groupedCode.set(key, gCode);

			this.sessions.addControl(raw.code, new UntypedFormControl(false));
		});

		this.sessionGroups = Array.from(grouped.entries()).map(([key, items]) => ({
			code: groupedCode.get(key) || key.toLowerCase().replace(/\s+/g, "_"),
			key,
			items,
		}));

		if (this.sessionGroups.length === 1) {
			this.singleCols = this.splitIntoColumns(this.sessionGroups[0].items, 4);
			this.twoColsLeft = undefined;
			this.twoColsRight = [];
			this.twoGroupsCompact = false;
			this.secondGroupTitle = "";
		} else if (this.sessionGroups.length === 4) {
			this.twoColsLeft = this.sessionGroups[0];
			const rightItems = this.sessionGroups[1].items;
			const thirdItems = this.sessionGroups[2].items;
			this.twoGroupsCompact = rightItems.length <= 4;
			this.twoColsRight = this.twoGroupsCompact ? [] : this.splitIntoColumns(rightItems, 2);
			this.singleCols = [];
			this.lastCols = this.splitIntoColumns(thirdItems, 2);
			this.lastColTitle = this.sessionGroups[2].key;
			this.secondGroupTitle = this.sessionGroups[1].key;
		} else if (this.sessionGroups.length === 3) {
			this.twoColsLeft = this.sessionGroups[0];
			const rightItems = this.sessionGroups[1].items;
			this.twoGroupsCompact = rightItems.length <= 4;
			this.twoColsRight = this.twoGroupsCompact ? [] : this.splitIntoColumns(rightItems, 2);
			this.singleCols = [];
			this.secondGroupTitle = this.sessionGroups[1].key;
		} else {
			this.singleCols = [];
			this.twoColsLeft = undefined;
			this.twoColsRight = [];
			this.twoGroupsCompact = false;
			this.secondGroupTitle = "";
		}

		this.sessions.updateValueAndValidity({ onlySelf: true });
		this.wireUpNotRequiredMutualExclusion();
	}

	// ---------- Maneuver builders / helpers ----------
	private makeObsValue() {
		return {
			totalCount: 0,
			currentCount: 0,
			preDefined: true,
			lastAction: CONSTANT.OB_LAST_ACTION.EDIT,
		};
	}

	private buildManeuverFromObservations(observations: Observation[], formType: string): UntypedFormGroup {
		const byCategory = new Map<string, Array<{ key: string; num: number }>>();
		const seen = new Set<string>();

		for (const o of observations || []) {
			const code = (o.code || "").trim();
			if (!code || seen.has(code)) continue;
			seen.add(code);

			const m = /^([a-zA-Z]+)_(\d+)$/.exec(code);
			if (!m) continue;
			const cat = m[1].toUpperCase();
			const num = Number(m[2]);
			const key = `${cat}${num}`;
			if (!byCategory.has(cat)) byCategory.set(cat, []);
			byCategory.get(cat)!.push({ key, num });
		}

		const categoryGroups: Record<string, any> = {};
		for (const [cat, items] of byCategory.entries()) {
			items.sort((a, b) => a.num - b.num);
			const controls: Record<string, any> = {};
			for (const it of items) {
				controls[it.key] = [{ value: this.makeObsValue(), disabled: false }];
			}
			categoryGroups[cat] = this.fb.group(controls);
		}

		// attach per-maneuver validator
		return this.fb.group({
			maneuver: ["", [Validators.required]],
			formType: [formType],
			maneuverCode: [""],
			assessmentId: [""],
			traineeAssessmentId: [""],
			candidateId: [""],
			selectedOBs: [[]],
			safetyOutcome: [-2],
			repeat: [0],
			...categoryGroups,
		});
	}

	private buildManeuverCrewFromObservations(observations: Observation[], formType: string): UntypedFormGroup {
		const byCategory = new Map<string, Array<{ key: string; num: number }>>();
		const seen = new Set<string>();

		for (const o of observations || []) {
			const code = (o.code || "").trim();
			if (!code || seen.has(code)) continue;
			seen.add(code);

			const m = /^([a-zA-Z]+(?:_[a-zA-Z]+)*)_(\d+)$/.exec(code);
			if (!m) continue;
			const cat = m[1].toUpperCase();
			const num = Number(m[2]);
			const key = `${cat}${num}`;
			if (!byCategory.has(cat)) byCategory.set(cat, []);
			byCategory.get(cat)!.push({ key, num });
		}

		const categoryGroups: Record<string, any> = {};
		for (const [cat, items] of byCategory.entries()) {
			items.sort((a, b) => a.num - b.num);
			const controls: Record<string, any> = {};
			for (const it of items) controls[it.key] = [{ value: this.makeObsValue(), disabled: false }];
			categoryGroups[cat] = this.fb.group(controls);
		}

		// attach per-maneuver validator
		return this.fb.group({
			maneuver: ["", [Validators.required]],
			formType: [formType],
			maneuverCode: [""],
			assessmentId: [""],
			traineeAssessmentId: [""],
			candidateId: [""],
			selectedOBs: [[]],
			safetyOutcome: [-2],
			repeat: [0],
			...categoryGroups,
		});
	}

	/** Rehydrate counts into OB controls */
	private patchAttemptsIntoManeuver(
		mfg: UntypedFormGroup,
		obs: Array<{ code?: string; observationType?: string; attempt: number }>,
	) {
		for (const o of obs || []) {
			const raw = (o.code ?? o.observationType ?? "").trim();
			const m = /^([a-zA-Z]+(?:_[a-zA-Z]+)*)_(\d+)$/.exec(raw);
			if (!m) continue;

			const cat = m[1].toUpperCase();
			const key = `${cat}${Number(m[2])}`;
			const catGroup = mfg.get(cat) as UntypedFormGroup | null;
			const control = catGroup?.get(key);
			if (control) {
				const cur = control.value || this.makeObsValue();
				control.patchValue(
					{
						...cur,
						totalCount: o.attempt,
						currentCount: o.attempt,
						preDefined: true,
						lastAction: CONSTANT.OB_LAST_ACTION.EDIT,
					},
					{ emitEvent: false },
				);
			}
		}
		// ensure validator runs after patch
		mfg.updateValueAndValidity({ onlySelf: true });
	}

	/** For non-TS1 header display/payload shape */
	private setManeuverOnGroup(mfg: UntypedFormGroup, opt: { code: string; description?: string }) {
		if (this.isTS1) {
			mfg.get("maneuver")?.patchValue({ code: opt.code, description: opt.description ?? "" });
		} else {
			mfg.get("maneuver")?.patchValue(opt.code);
		}
		mfg.get("maneuver")?.markAsDirty();
		mfg.get("maneuver")?.updateValueAndValidity({ emitEvent: false });
	}

	// ---------- Load ----------
	private fetchFormById() {
		this.loadingService.setLoading(true);
		this.httpService.getForm(this.formId).subscribe({
			next: (resp) => this.loadForm(unwrapFormApiResponse<TS1FormResponseJSON>(resp)),
			error: () => this.loadingService.setLoading(false),
		});
	}

	private patchReminderNotes(notes: Array<{ text: string; requireStatus?: boolean }> | undefined): void {
		patchReminderNotesFormArray(this.fb, this.reminderNotes, notes ?? []);
	}

	private loadForm(resp: TS1FormResponseJSON): void {
		// Basic fields
		this.formType.patchValue(resp.formType);
		this.formName.patchValue(resp.name);
		this.createNewForm.get("ets1SessionVersion")?.setValue(resp.ets1SessionVersion ?? 1, { emitEvent: false });
		this.patchReminderNotes(resp.reminderNotes ?? []);
		this.isTS1V2Form = inferFormConfigVersion(resp) === 2;
		this.formIsPublished =
			!!(resp as { publishedAt?: string; isPublished?: boolean }).publishedAt ||
			!!(resp as { isPublished?: boolean }).isPublished;

		if (this.isTS1V2Form) {
			patchTs1FormConfigFromResponse(this.reminderNotesEnabled, this.reminderNotesTemplate, resp);
		}

		this.today = new Date(resp.startDate);
		this.syllabusStartDate.patchValue(new Date(resp.startDate));
		this.syllabusEndDate.patchValue(new Date(resp.endDate));

		// Config for this formType
		const sessionVersion = isEts1SessionV2(resp) ? 2 : undefined;
		this.httpService
			.getFormConfig(
				resp.formType,
				sessionVersion,
				sessionVersion === 2 ? resolveEts1SessionAocCode(resp, this.roleService.getUserAOC()) : undefined,
			)
			.subscribe({
				next: (cfg: any) => {
					const {
						sessionConfigurations: sessionsRaw,
						observations,
						maneuvers: maneuversMeta,
					} = parseFormConfigResponse(cfg);

					// Push "others" group to the end
					const isOthers = (s: SessionCfgRaw) => s.session_conf_type?.code?.toLowerCase() === "other";

					const sessions: SessionCfgRaw[] = [
						...sessionsRaw.filter((s) => !isOthers(s)),
						...sessionsRaw.filter(isOthers),
					];

					this.lastObservations = observations as Observation[];
					this.fixedManeuvers = (maneuversMeta || []) as any[];

					// Maneuvers
					this.maneuvers.clear();

					const assess = (resp.assessmentConfig || []) as Ts1AssessmentConfigEntry[];
					const sortedAssess = [...assess].sort((a, b) => (a?.sequenceNumber ?? 0) - (b?.sequenceNumber ?? 0));
					const { maneuvers: maneuverAssessments, competencies } = splitTs1AssessmentConfig(sortedAssess);
					this.preservedCompetencyAssessments = competencies;

					if (this.isTS1V2) {
						if (this.isEts1V2) {
							this.rebuildEts1AocSessionControls(sessions);
							const sessionDetails = resp.sessionDetails ?? [];
							const selectedCodes = sessionDetails.map((s: { code: string }) => s.code);
							const remarksByCode = sessionDetails.reduce(
								(acc: Record<string, string>, s: { code: string; selectionRemarks?: string }) => {
									const remark = s.selectionRemarks?.trim();
									if (remark) acc[s.code] = remark;
									return acc;
								},
								{},
							);
							patchEts1SessionSelectionFromTemplate(
								this.sessions,
								this.sessionRemarks,
								selectedCodes,
								remarksByCode,
								this.ets1SessionColumns,
							);
						} else {
							this.rebuildSessionControls(sessions, true);
							const selectedCodes = new Set((resp.sessionDetails || []).map((s: any) => s.code));
							Object.keys(this.sessions.controls).forEach((code) => {
								this.sessions.get(code)?.patchValue(selectedCodes.has(code), { emitEvent: false });
							});
							patchTs1SessionRemarksFromResponse(this.sessionRemarks, resp.sessionDetails ?? []);
						}
						patchTs1FormConfigFromResponse(this.reminderNotesEnabled, this.reminderNotesTemplate, resp);
						this.reminderNotesSubs.forEach((s) => s.unsubscribe());
						this.reminderNotesSubs = [];
						wireTs1ReminderNotesClearing(this.reminderNotesEnabled, this.reminderNotesTemplate, this.reminderNotesSubs);
						this.sessions.updateValueAndValidity({ onlySelf: true });

						this.ts1FormManeuversOptions = filterTs1ManeuverOptions(maneuversMeta || []);

						maneuverAssessments.forEach((ac) => {
							const mfg = buildManeuverV2FromObservations(
								this.fb,
								observations.length ? observations : this.lastObservations,
								this.formType.value as FormDataOptions,
								CONSTANT.OB_LAST_ACTION.EDIT,
							);
							mfg.get("maneuver")?.patchValue(ac.maneuverType);
							patchAttemptsIntoManeuverV2(
								mfg,
								(ac.observations || []).map((o: any) => ({
									code: o.code ?? o.observationType,
									attempt: o.attempt,
									candidateRole: o.candidateRole,
									isConditional: o.isConditional,
								})),
							);
							if (ac.assessmentId) mfg.get("assessmentId")?.patchValue(ac.assessmentId, { emitEvent: false });
							this.maneuvers.push(mfg);
						});
					} else {
						if (isEts35FormType(resp.formType) && this.isEts1V2) {
							this.rebuildEts35SessionControls(sessions);
							const sessionDetails = resp.sessionDetails ?? [];
							const selectedCodes = sessionDetails.map((s: { code: string }) => s.code);
							const remarksByCode = sessionDetails.reduce(
								(acc: Record<string, string>, s: { code: string; selectionRemarks?: string }) => {
									const remark = s.selectionRemarks?.trim();
									if (remark) acc[s.code] = remark;
									return acc;
								},
								{},
							);
							patchEts1SessionSelectionFromTemplate(
								this.sessions,
								this.sessionRemarks,
								selectedCodes,
								remarksByCode,
								this.ets35SessionSections.flatMap((section) => section.columns),
							);
							enforceEts35CoursePurposeSingleSelection(this.ets35SessionSections, this.sessions, this.sessionRemarks);
						} else {
							this.rebuildSessionControls(sessions, this.isTS1);
							const selectedCodes = new Set((resp.sessionDetails || []).map((s: any) => s.code));
							Object.keys(this.sessions.controls).forEach((code) => {
								this.sessions.get(code)?.patchValue(selectedCodes.has(code), { emitEvent: false });
							});
							if (this.isTS1) {
								patchTs1SessionRemarksFromResponse(this.sessionRemarks, resp.sessionDetails ?? []);
							}
						}
						this.sessions.updateValueAndValidity({ onlySelf: true });

						const acByCode = new Map<string, any>();
						for (const ac of maneuverAssessments) {
							const mvVal = ac?.maneuverType;
							const mvCode = mvVal && typeof mvVal === "object" ? mvVal.code : mvVal;
							if (mvCode) acByCode.set(String(mvCode).toLowerCase(), ac);
						}

						if (this.isTS1) {
							this.ts1FormManeuversOptions = filterTs1ManeuverOptions(maneuversMeta || []);

							maneuverAssessments.forEach((ac) => {
								let obsForGroup = observations;
								if (!obsForGroup.length) obsForGroup = this.lastObservations || [];

								const mfg = this.buildManeuverFromObservations(obsForGroup, this.formType.value as any);
								mfg.get("maneuver")?.patchValue(ac.maneuverType);

								this.patchAttemptsIntoManeuver(
									mfg,
									(ac.observations || []).map((o: any) => ({
										code: o.code ?? o.observationType,
										attempt: o.attempt,
									})),
								);

								if (ac.assessmentId) mfg.get("assessmentId")?.patchValue(ac.assessmentId, { emitEvent: false });

								this.maneuvers.push(mfg);
							});

							if (this.maneuvers.length === 0) {
								const mfg = this.buildManeuverFromObservations(observations, this.formType.value as any);
								this.maneuvers.push(mfg);
							}
						} else {
							const ft = this.formType.value as FormDataOptions;

							if (Array.isArray(maneuversMeta) && maneuversMeta.length) {
								maneuversMeta.forEach((m, idx) => {
									let obsForGroup = observations.filter(
										(o) => (o.fixed_maneuver ?? "").toLowerCase() === String(m.code).toLowerCase(),
									);
									if (!obsForGroup.length) obsForGroup = observations;

									const mfg =
										this.isCabinCrew || this.isCabinCrewInstructor
											? this.buildManeuverCrewFromObservations(obsForGroup, this.formType.value as any)
											: this.buildManeuverFromObservations(obsForGroup, this.formType.value as any);

									if (this.isTS6) {
										this.setManeuverOnGroup(mfg, { code: ft.code, description: ft.description });
									} else {
										this.setManeuverOnGroup(mfg, { code: m.code, description: m.description });
									}

									const acMatch = acByCode.get(String(m.code).toLowerCase()) ?? sortedAssess[idx] ?? null;

									if (acMatch) {
										this.patchAttemptsIntoManeuver(
											mfg,
											(acMatch.observations || []).map((o: any) => ({
												code: o.code ?? o.observationType,
												attempt: o.attempt,
											})),
										);
										if (acMatch.assessmentId)
											mfg.get("assessmentId")?.patchValue(acMatch.assessmentId, { emitEvent: false });
									}

									this.maneuvers.push(mfg);
								});
							} else {
								const base = observations.length ? observations : this.lastObservations;
								const mfg =
									this.isCabinCrew || this.isCabinCrewInstructor
										? this.buildManeuverCrewFromObservations(base || [], this.formType.value as any)
										: this.buildManeuverFromObservations(base || [], this.formType.value as any);

								if (this.isTS6 && ft) {
									this.setManeuverOnGroup(mfg, { code: ft.code, description: ft.description });
								}
								this.maneuvers.push(mfg);
							}
						}
					}

					// keep validators aligned with *current* form type and clear stale TS-1 errors if needed
					this.refreshManeuverValidatorsForFormType();

					// Touch & validate so errors appear immediately on edit
					this.maneuvers.markAllAsTouched();
					this.maneuvers.controls.forEach((ctrl) => ctrl.updateValueAndValidity({ onlySelf: true }));
					this.maneuvers.updateValueAndValidity({ onlySelf: true });

					this.createNewForm.markAsPristine();
					if (this.isReadOnly) {
						this.applyReadOnlyMode();
					}
					this.cd.detectChanges();
					this.loadingService.setLoading(false);
				},
				error: () => this.loadingService.setLoading(false),
			});
	}

	// ---------- Date handlers (store Dates; keep end ≥ start) ----------
	private parseDateOnlyStrict(s: string): Date | null {
		const m = moment(s, this.DISPLAY_FMT, true); // strict
		if (!m.isValid()) return null;
		// stabilize to noon to avoid DST-midnight shenanigans
		return m.hour(12).minute(0).second(0).millisecond(0).toDate();
	}

	handleStartDateChange(dateString: string): void {
		if (this.isReadOnly) return;
		if (isEmpty(dateString)) return;

		const start = new Date(dateString);
		if (!start) {
			this.syllabusStartDate.setErrors({ invalidDate: true });
			return;
		}

		const endVal = this.syllabusEndDate.value;
		const end = endVal instanceof Date ? new Date(endVal.getTime()) : this.parseDateOnlyStrict(String(endVal));

		this.syllabusStartDate.patchValue(start);
		// keep end >= start
		if (!end || end < start) this.syllabusEndDate.patchValue(start);
	}

	handleEndDateChange(dateString: string): void {
		if (this.isReadOnly) return;
		if (isEmpty(dateString)) return;

		const end = new Date(dateString);
		if (!end) {
			this.syllabusEndDate.setErrors({ invalidDate: true });
			return;
		}

		const startVal = this.syllabusStartDate.value;
		const start = startVal instanceof Date ? new Date(startVal.getTime()) : this.parseDateOnlyStrict(String(startVal));

		this.syllabusEndDate.patchValue(!start || end >= start ? end : start);
	}

	// ---------- UI actions ----------
	private applyReadOnlyMode(): void {
		this.createNewForm.disable({ emitEvent: false });
	}

	saveFormType(value: FormDataOptions): void {
		if (this.isReadOnly) return;
		this.formType.patchValue(value);
	}

	saveManeuver(index: number, value: FormDataOptions, panel?: MatExpansionPanelHeader): void {
		if (this.isReadOnly) return;
		const control = this.maneuvers.at(index).get("maneuver");
		control?.patchValue(value);
		control?.markAsDirty();
		if (panel && !(panel as any)._isExpanded()) (panel as any)._toggle();

		// revalidate this maneuver (so error clears as soon as an OB is added)
		this.maneuvers.at(index).updateValueAndValidity({ onlySelf: true });
		this.maneuvers.updateValueAndValidity({ onlySelf: true });
	}

	removeManeuver(idx: number) {
		if (this.isReadOnly) return;
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "remove-maneuver-modal",
			height: "240px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Confirmation",
				modalContent: "You are about to delete this Maneuver and all associated OBs. Are you sure you want to proceed?",
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: { close?: boolean }) => {
			if (result?.close === true) {
				this.confirmRemoveManeuver(idx);
			}
			sub.unsubscribe();
		});
	}

	private confirmRemoveManeuver(idx: number) {
		this.maneuvers.removeAt(idx);
		this.maneuvers.updateValueAndValidity({ onlySelf: true });
	}

	addNewManeuver() {
		if (this.isReadOnly) return;
		if (this.isTS1V2) return;
		const obs = this.isTS1
			? this.lastObservations || []
			: this.isTS6
				? (this.lastObservations || []).filter(
						(o) =>
							(o.fixed_maneuver ?? "").toLowerCase() ===
							String((this.formType.value as FormDataOptions)?.code).toLowerCase(),
					)
				: this.lastObservations || [];

		const base = obs && obs.length ? obs : this.lastObservations || [];
		const mfg =
			this.isCabinCrew || this.isCabinCrewInstructor
				? this.buildManeuverCrewFromObservations(base, this.formType.value as any)
				: this.buildManeuverFromObservations(base, this.formType.value as any);

		if (!this.isTS1) {
			const ft = this.formType.value as FormDataOptions;
			this.setManeuverOnGroup(mfg, { code: ft.code, description: ft.description });
		}

		this.maneuvers.push(mfg);

		// align validator with current type for the newly added maneuver
		this.refreshManeuverValidatorsForFormType();

		// show error immediately if nothing selected in the new panel
		mfg.markAllAsTouched();
		mfg.updateValueAndValidity({ onlySelf: true });
		this.maneuvers.updateValueAndValidity({ onlySelf: true });

		this.cd.detectChanges();
	}

	onAddManeuverV2(selected: FormDataOptions) {
		if (this.isReadOnly) return;
		const mfg = buildManeuverV2FromObservations(
			this.fb,
			this.lastObservations || [],
			this.formType.value as FormDataOptions,
			CONSTANT.OB_LAST_ACTION.EDIT,
		);
		mfg.get("maneuver")?.patchValue(selected);
		this.maneuvers.push(mfg);
		this.maneuvers.updateValueAndValidity({ onlySelf: true });
		this.cd.detectChanges();
	}

	onRemoveManeuverV2(idx: number) {
		this.confirmRemoveManeuver(idx);
	}

	onDropManeuver(event: CdkDragDrop<any[]>) {
		if (this.isReadOnly || !this.isTS1) return;

		const fa = this.maneuvers;
		const prev = event.previousIndex;
		const curr = event.currentIndex;

		if (prev === curr) return;

		const ctrl = fa.at(prev);
		fa.removeAt(prev);
		fa.insert(curr, ctrl);

		fa.updateValueAndValidity({ onlySelf: true });
		this.cd.detectChanges();
	}

	showExpansion(maneuver: AbstractControl | null): boolean {
		if (!maneuver) return false;

		const v = (maneuver as any)?.value;
		if (!v || typeof v !== "object") return false;

		const baseFields = new Set([
			"maneuver",
			"maneuverCode",
			"assessmentId",
			"traineeAssessmentId",
			"candidateId",
			"selectedOBs",
			"safetyOutcome",
			"repeat",
			"formType",
		]);

		const remainingKeys = Object.keys(v).filter((k) => !baseFields.has(k));
		return remainingKeys.length > 1;
	}

	// ---------- Helpers ----------
	private focusFirstInvalidControl(): void {
		const invalid: HTMLElement | null =
			document.querySelector("[formControlName].ng-invalid") ||
			document.querySelector("[formArrayName].ng-invalid") ||
			document.querySelector("[formGroupName].ng-invalid");

		if (invalid && typeof invalid.scrollIntoView === "function") {
			invalid.scrollIntoView({ behavior: "smooth", block: "center" });
			(invalid as HTMLInputElement).focus?.();
		}
	}

	private sessionColumnsForValidation(): Ets1SessionColumnVM[] {
		return this.isEts35SessionCatalog ? ets35TrainingColumns(this.ets35SessionSections) : this.ets1SessionColumns;
	}

	private missingRequiredSessionText(): boolean {
		if (!this.isEts1V2 || (!this.isTS1 && !this.isEts35SessionCatalog)) return false;
		return hasMissingRequiredSessionText(this.sessions, this.sessionRemarks, this.sessionColumnsForValidation());
	}

	private missingRequiredEts1RadioSelection(): boolean {
		if (!this.isEts1V2 || (!this.isTS1 && !this.isEts35SessionCatalog)) return false;
		return markMissingRequiredEts1RadioSelection(this.sessions, this.sessionColumnsForValidation());
	}

	// ---------- Submit ----------
	onSubmit(): void {
		if (this.isReadOnly) {
			return;
		}

		// Prevent double submission
		if (this.isSubmitting) {
			return;
		}

		// Mark all as touched first
		this.createNewForm.markAllAsTouched();
		this.sessions.markAllAsTouched();
		this.sessionRemarks.markAllAsTouched();
		this.maneuvers.markAllAsTouched();

		// Ensure all maneuver validators fire - update children first, then parent
		// Use onlySelf: false to ensure validation propagates to parent form
		this.maneuvers.controls.forEach((ctrl) => ctrl.updateValueAndValidity({ onlySelf: false }));
		this.maneuvers.updateValueAndValidity({ onlySelf: false });
		this.sessions.updateValueAndValidity({ onlySelf: false });
		this.createNewForm.updateValueAndValidity();

		// Trigger change detection to ensure validation state is updated synchronously
		this.cd.detectChanges();

		if (this.createNewForm.invalid) {
			this.focusFirstInvalidControl();
			this.scrollToTop();
			return;
		}

		if (this.isTS1V2 && this.maneuvers.length === 0) {
			this.scrollToTop();
			return;
		}

		if (this.missingRequiredSessionText()) {
			this.scrollToTop();
			return;
		}

		if (this.missingRequiredEts1RadioSelection()) {
			this.scrollToTop();
			return;
		}

		if (
			this.isTS1V2 &&
			!this.isEts1V2 &&
			this.reminderNotesEnabled.value &&
			!String(this.reminderNotesTemplate.value ?? "").trim()
		) {
			this.reminderNotesTemplate.setErrors({ required: true });
			this.reminderNotesTemplate.markAsTouched();
			this.scrollToTop();
			return;
		}

		// Set submitting flag to prevent double submission
		this.isSubmitting = true;

		let body: TS1FormRequestJSON;

		if (this.isTS1V2) {
			body = buildV2RequestPayload(
				this.createNewForm.value,
				this.roleService.getUserAOC(),
				this.DATE_FORMAT.FORMS_BE_DATE_FORMAT,
				moment,
				this.isTS1 ? this.ets1SessionColumns : [],
			);
			// preserve assessmentId and sequence on update
			body.assessmentConfig = body.assessmentConfig.map((ac, idx) => {
				const mv = this.maneuvers.at(idx)?.value;
				return {
					...ac,
					assessmentId: mv?.assessmentId || undefined,
					sequenceNumber: idx + 1,
				} as any;
			});
		} else {
			const rawValue = this.createNewForm.getRawValue() as TS1Form & { sessionRemarks?: Record<string, string> };
			const formValue = rawValue;

			const sessionPayload = this.isEts35SessionCatalog
				? buildAdminFormSessionPayload(
						(formValue.sessions ?? {}) as unknown as Record<string, boolean>,
						formValue.sessionRemarks ?? {},
						this.ets35SessionSections.flatMap((section) => section.columns),
					)
				: {
						sessionDetails: Object.entries(formValue.sessions ?? {})
							.filter(([, selected]) => selected)
							.map(([code]) => code),
						sessionRemarksByCode: buildSessionRemarksByCode(
							formValue.sessions as unknown as Record<string, boolean>,
							formValue.sessionRemarks ?? {},
						),
					};
			const sessionCodes = sessionPayload.sessionDetails;

			// maneuvers → assessmentConfig
			type OB = { observationType: string; attempt: number };
			const baseFields = this.BASE_OB_KEYS;
			const maneuvers = Array.isArray(formValue.maneuvers) ? formValue.maneuvers : [];

			const assessmentConfig = appendTs1CompetencyAssessments(
				maneuvers.reduce((acc: any[], mv: any, idx: number) => {
					const selectedOBs: OB[] = [];

					Object.keys(mv ?? {}).forEach((catKey) => {
						if (baseFields.has(catKey)) return;

						const grp = mv[catKey] ?? {};
						Object.keys(grp).forEach((obKey) => {
							const c = grp[obKey];
							const m = /^([A-Z]+(?:_[A-Z]+)*)(\d+)$/.exec(obKey);
							if (m && c?.currentCount > 0) {
								selectedOBs.push({
									observationType: `${m[1].toLowerCase()}_${m[2]}`,
									attempt: c.currentCount,
								});
							}
						});
					});

					const maneuverType = typeof mv?.maneuver === "string" ? mv.maneuver : mv?.maneuver?.code;
					if (maneuverType) {
						acc.push({
							maneuverType,
							observations: selectedOBs,
							sequenceNumber: idx + 1,
							assessmentId: mv.assessmentId || undefined,
						});
					}
					return acc;
				}, []),
				this.preservedCompetencyAssessments,
			);

			body = {
				name: formValue.formName,
				startDate: moment(formValue.syllabusStartDate).format(this.DATE_FORMAT.FORMS_BE_DATE_FORMAT),
				endDate: moment(formValue.syllabusEndDate).format(this.DATE_FORMAT.FORMS_BE_DATE_FORMAT),
				formType: (formValue.formType as any).code,
				sessionDetails: sessionCodes,
				...(Object.keys(sessionPayload.sessionRemarksByCode ?? {}).length > 0
					? { sessionRemarksByCode: sessionPayload.sessionRemarksByCode }
					: {}),
				assessmentConfig: assessmentConfig as any,
				aocCode: this.roleService.getUserAOC(),
				...(isEts35FormType(this.formType.value as FormDataOptions | string | null)
					? { ets1SessionVersion: Number(this.createNewForm.get("ets1SessionVersion")?.value ?? 1) }
					: {}),
			};
		}

		this.formErrorMessage = "";
		this.loadingService.setLoading(true);
		this.httpService.udateForm(this.formId, body).subscribe({
			next: () => this.onSuccessFromSubmit(),
			error: (error) => this.onErrorFormSubmit(error),
		});
	}

	onSuccessFromSubmit() {
		this.formSuccess = true;
		this.formErrorMessage = "";
		this.isSubmitting = false;
		setTimeout(() => this.router.navigate([RouteType.FORMS]), 1500);
		this.loadingService.setLoading(false);
	}

	onErrorFormSubmit(error: unknown) {
		this.formSuccess = false;
		this.formErrorMessage = resolveHttpErrorMessageText(error);
		this.snackBar.openFromComponent(ErrorSnackbarComponent, {
			data: { message: this.formErrorMessage } as ErrorSnackbarData,
			duration: 5000,
			horizontalPosition: "center",
			verticalPosition: "top",
			panelClass: ["error-snackbar"],
		});
		this.isSubmitting = false;
		this.loadingService.setLoading(false);
		this.scrollToTop();
		this.cd.markForCheck();
	}

	hasUnsavedFormChanges(): boolean {
		if (this.isReadOnly) {
			return false;
		}
		return !this.formSuccess && !this.isSubmitting && this.createNewForm?.dirty === true;
	}

	goBack(): void {
		const queryParams = this.route.snapshot.queryParams;
		void this.router.navigate([RouteType.FORMS], { queryParams });
	}
}
